import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Chip from "@mui/material/Chip";
import { useTheme } from "@mui/material/styles";
import { Link } from "react-router-dom";

import type { CellParams, DataTableColDef } from "../../DataTable";
import { redactText, renderCellRedacted } from "../../redact";
import {
  OPS_CATEGORY_LABELS,
  OPS_MFA_TYPE,
  OPS_MFA_TYPE_LABELS,
  OPS_OWNER_LABELS,
  OPS_STATUS,
  OPS_STATUS_LABELS,
  type OpsCategory,
  type OpsMfaType,
  type OpsOwner,
  type OpsStatus,
} from "../common";
import { MaskValue } from "../ui/Mask";
import { TotpCode } from "../ui/TotpCode";

type StatusPalette = { bg: string; border: string; color: string };

const STATUS_COLORS: Record<OpsStatus, StatusPalette> = {
  ACTIVE: { bg: "#ECFDF3", border: "#ABEFC6", color: "#067647" },
  INACTIVE: { bg: "#F2F4F7", border: "#EAECF0", color: "#475467" },
  INVALID: { bg: "#FEF3F2", border: "#FECDCA", color: "#F04438" },
  PENDING: { bg: "#FFFAEB", border: "#FEDF89", color: "#B54708" },
  LOCKED: { bg: "#FEF3F2", border: "#FECDCA", color: "#F04438" },
};

function StatusChip({ value }: { value: string }) {
  const status = (
    value in STATUS_COLORS ? value : OPS_STATUS.INACTIVE
  ) as OpsStatus;
  const palette = STATUS_COLORS[status];
  return (
    <Chip
      label={OPS_STATUS_LABELS[status]}
      sx={{
        height: "22px",
        backgroundColor: palette.bg,
        border: `solid 1px ${palette.border}`,
        color: palette.color,
        fontSize: "12px",
        fontWeight: 500,
      }}
    />
  );
}

export type OpsRow = OpsCredentialsMessage & {
  unmaskValue: (name: string) => Promise<string>;
  fetchTotp: () => Promise<TotpMessage>;
};

type OpsCellParams = CellParams<OpsRow>;

export function getOpsColumns(
  openEdit: (row: OpsRow) => void,
  openDelete: (row: OpsRow) => void,
  targetOptions: string[],
): DataTableColDef<OpsRow>[] {
  return [
    {
      field: "practiceName",
      headerName: "Practice",
      flex: 1,
      renderCell: ({ value }: OpsCellParams) => (
        <div data-sr-redact>{redactText(value as string | null)}</div>
      ),
    },
    {
      field: "category",
      headerName: "Category",
      type: "singleSelect",
      valueOptions: Object.keys(OPS_CATEGORY_LABELS),
      flex: 1,
      renderCell: ({ value }: OpsCellParams) =>
        value ? (
          <Chip
            label={OPS_CATEGORY_LABELS[value as OpsCategory] ?? value}
            size="small"
            sx={{
              backgroundColor: "#F2F4F7",
              color: "#344054",
              fontSize: "12px",
              fontWeight: 500,
            }}
          />
        ) : (
          "—"
        ),
    },
    {
      field: "owner",
      headerName: "Owner",
      type: "singleSelect",
      valueOptions: Object.keys(OPS_OWNER_LABELS),
      flex: 1,
      renderCell: ({ value }: OpsCellParams) => (
        <div>{value ? (OPS_OWNER_LABELS[value as OpsOwner] ?? value) : ""}</div>
      ),
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
    },
    {
      field: "targetName",
      headerName: "Target",
      type: "singleSelect",
      valueOptions: targetOptions,
      flex: 1,
      renderCell: ({ value }: OpsCellParams) => (
        <div>{(value as string | null) || "—"}</div>
      ),
    },
    {
      field: "website",
      headerName: "Website",
      flex: 1,
      renderCell: ({ value }: OpsCellParams) => {
        const theme = useTheme();
        const website = value as string | null;
        if (!website) {
          return <div>—</div>;
        }
        return (
          <Link
            style={{
              textDecoration: "underline",
              color: theme.palette.primary.main,
            }}
            target="_blank"
            rel="noopener noreferrer"
            to={website}
          >
            <div>{website}</div>
          </Link>
        );
      },
    },
    {
      field: "username",
      headerName: "Username",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (props: OpsCellParams) => (
        <MaskValue unmaskValue={async () => props.row.unmaskValue("username")}>
          {renderCellRedacted(props)}
        </MaskValue>
      ),
    },
    {
      field: "password",
      headerName: "Password",
      flex: 1.25,
      sortable: false,
      filterable: false,
      renderCell: (props: OpsCellParams) => (
        <MaskValue unmaskValue={async () => props.row.unmaskValue("password")}>
          {renderCellRedacted(props)}
        </MaskValue>
      ),
    },
    {
      field: "mfaType",
      headerName: "MFA",
      type: "singleSelect",
      valueOptions: Object.keys(OPS_MFA_TYPE_LABELS),
      flex: 1,
      sortable: false,
      renderCell: (props: OpsCellParams) => {
        const type = props.row.mfaType as OpsMfaType | null;
        if (!type || type === OPS_MFA_TYPE.NONE) {
          return <div>—</div>;
        }
        if (type === OPS_MFA_TYPE.AUTHENTICATOR) {
          return <TotpCode fetchCode={() => props.row.fetchTotp()} />;
        }
        if (type === OPS_MFA_TYPE.EMAIL) {
          return (
            <MaskValue
              unmaskValue={async () => props.row.unmaskValue("mfa_email")}
            >
              <div>{OPS_MFA_TYPE_LABELS.EMAIL}</div>
            </MaskValue>
          );
        }
        if (type === OPS_MFA_TYPE.SMS) {
          return (
            <MaskValue
              unmaskValue={async () => props.row.unmaskValue("mfa_phone")}
            >
              <div>{OPS_MFA_TYPE_LABELS.SMS}</div>
            </MaskValue>
          );
        }
        return <div>{OPS_MFA_TYPE_LABELS[type] ?? "—"}</div>;
      },
    },
    {
      field: "status",
      headerName: "Status",
      type: "singleSelect",
      valueOptions: Object.keys(OPS_STATUS_LABELS),
      flex: 1,
      renderCell: ({ value }: OpsCellParams) => (
        <StatusChip value={value as string} />
      ),
    },
    {
      field: "notes",
      headerName: "Notes",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (props: OpsCellParams) =>
        props.row.notes ? (
          <MaskValue unmaskValue={async () => props.row.unmaskValue("notes")}>
            {renderCellRedacted(props)}
          </MaskValue>
        ) : (
          <div>—</div>
        ),
    },
    {
      field: "createdBy",
      headerName: "Added by",
      flex: 1,
      renderCell: ({ value }: OpsCellParams) => (
        <div>{(value as string | null) || ""}</div>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Updated",
      flex: 1,
      renderCell: ({ value }: OpsCellParams) => (
        <div>
          {value ? new Date(value as string).toLocaleDateString() : "—"}
        </div>
      ),
    },
    {
      field: "settings",
      type: "actions",
      getActions: ({ row }) => [
        { label: "Edit", icon: <EditIcon />, onClick: () => openEdit(row) },
        {
          label: "Delete",
          icon: <DeleteIcon />,
          onClick: () => openDelete(row),
        },
      ],
    },
  ];
}
